import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ImageOff } from 'lucide-react';
import { sizedImageUrl, altArtUrl, thumbFallbackUrl, ladderWidth, IMG } from '../utils/imageUrl';

/**
 * Universal card image component with fallback chain:
 * 1. Digital image (TCGdex/community)
 * 2. User's photo (their scan)
 * 3. Text placeholder with card info
 *
 * Props:
 * - digitalImage: clean digital art URL
 * - userPhoto: user's own photo URL
 * - name: card name (for placeholder)
 * - number: card number (for placeholder)
 * - setName: set name (for placeholder)
 * - preferPhoto: if true, show user photo first (for "photo" view mode)
 * - style: container style overrides
 * - imgStyle: img tag style overrides
 * - theme: theme object
 * - loading: "lazy" | "eager" (default "lazy")
 * - thumb: request art sized for a grid tile rather than a full-bleed view.
 * - width: explicit pixel width to request. Overrides the measurement below,
 *   and 0 means the untouched original, which is what a zoomable full-screen
 *   view wants. Leave it unset and the component asks for the size the tile
 *   turned out to be.
 * - pending: the caller is still resolving which artwork this card has. The
 *   difference between "not here yet" and "this card has no art" is invisible
 *   from in here, and drawing the second while the first is true is what made a
 *   loading grid look broken.
 * - onClick: click handler
 */

export default function CardImage({
  digitalImage,
  userPhoto,
  name,
  number,
  setName,
  preferPhoto = false,
  style = {},
  imgStyle = {},
  theme,
  // Native lazy-loading does not fire in this app's layout - the shell is
  // position:fixed with its own overflow scroller, which defeats the browser's
  // heuristics, so images were never requested at all. Forcing eager fixed the
  // blank tiles and created a worse one: a set grid renders every card it has,
  // so opening a 331-card set fired 331 image requests at once and the browser
  // serviced them six at a time.
  //
  // The real fix is an IntersectionObserver pointed at that custom scroller,
  // which is below. "lazy" now genuinely means lazy.
  loading = 'lazy',
  thumb = false,
  width,
  pending = false,
  onClick,
}) {
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  // Deferred until the tile is near the viewport. Eager callers skip this
  // entirely and request straight away, which is right for card detail and for
  // anything known to be on screen at first paint.
  const [inView, setInView] = useState(loading !== 'lazy');
  const holderRef = useRef(null);

  // How wide this tile actually is, in device pixels, snapped to the ladder.
  //
  // This is the whole loading fix and it lives here on purpose. Threading a
  // width through forty-one call sites means forty-one guesses that drift out of
  // date the next time a grid changes its column count or gains a zoom level,
  // and the top ten is exactly what that costs: five across at about 70pt, and
  // it was being handed the same 350px art as a three-across tile nearly twice
  // its size. The element knows its own width, so ask it.
  //
  // Null until measured. An explicit `width` skips this entirely - card detail
  // and the pinch-zoom view know what they need and must not be shrunk.
  const [measured, setMeasured] = useState(null);
  // Nothing is requested before the tile has been measured, or the first frame
  // would fetch the default width and the measurement would immediately fetch a
  // second file - two downloads per tile, which is worse than the bug. The
  // measurement is a layout effect, so this costs no paint and no round trip.
  const [sized, setSized] = useState(width !== undefined);
  useLayoutEffect(() => {
    if (width !== undefined) { setSized(true); return undefined; }
    const node = holderRef.current;
    if (!node) { setSized(true); return undefined; }

    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio : 1;
    const read = (px) => {
      // jsdom, and any container that has not laid out yet, report 0. Leaving
      // `measured` null there is right: a wrong-sized request is a slow tile,
      // an unmeasurable one must not become a missing one, so the prop default
      // still applies.
      if (px <= 0) return;
      const rung = ladderWidth(px, dpr);
      setMeasured((prev) => (prev === rung ? prev : rung));
    };

    // clientWidth, not getBoundingClientRect: several sheets in this app animate
    // in from scale(0.94), and a bounding rect is post-transform, so a tile read
    // mid-animation would report a width it is not going to keep. Layout width
    // is the honest number and is what ResizeObserver reports below.
    read(node.clientWidth);
    setSized(true);

    // A grid's zoom control changes the column count without remounting the
    // tiles, so the width has to be watched rather than read once. Zooming in
    // then genuinely gets a sharper file instead of an upscaled thumbnail.
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) read(entry.contentRect.width);
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, [width]);

  useEffect(() => {
    if (inView) return undefined;
    const node = holderRef.current;
    if (!node) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }

    // The app scrolls inside .app-content, not the document, so the observer
    // has to be told that or it measures against a viewport nothing moves in.
    // This is the same custom scroller that defeated native lazy loading.
    const root = node.closest('.app-content') || null;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      // Fetch about a screen ahead so tiles have arrived by the time they
      // scroll in, rather than popping in behind the scroll.
      { root, rootMargin: '600px 0px', threshold: 0.01 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [inView]);

  // Candidate chain, tried in order: the resized version first, then the
  // original behind it. A set with no low-res file, or a storage object the
  // render endpoint cannot process, falls through to the full image rather than
  // showing an empty tile - a slow card beats a blank one.
  const targetWidth = width !== undefined
    ? width
    : (measured ?? (thumb ? IMG.TILE : IMG.CARD));
  const digitalSized = sizedImageUrl(digitalImage, targetWidth);
  const photoSized = sizedImageUrl(userPhoto, targetWidth);
  // A second catalogue, for the cards the first one has no artwork for. Tried
  // after TCGdex and before the owner's photo, because it is the same clean art
  // the rest of the app shows.
  const altArt = altArtUrl(digitalImage);
  // A stored thumbnail that is not there yet (art mirrored since the last
  // prime-thumbs run) steps to the render endpoint, which is what every tile
  // used before, rather than to the 100KB original.
  const thumbFallback = thumbFallbackUrl(digitalSized, targetWidth);

  const candidates = (preferPhoto
    ? [photoSized, userPhoto, digitalSized, thumbFallback, digitalImage, altArt]
    : [digitalSized, thumbFallback, digitalImage, altArt, photoSized, userPhoto]
  ).filter((u, i, arr) => u && arr.indexOf(u) === i);

  const [attempt, setAttempt] = useState(0);

  // A new source must start from a clean slate, or a previously-failed or
  // previously-loaded image leaves its flags behind on the next card.
  //
  // It must NOT run on mount: effects fire after commit, and for an image that
  // resolves immediately onLoad has already set imgLoaded. Resetting here on
  // the first pass wiped that back to false and left every image at opacity 0.
  const lastSrcRef = useRef(null);
  useEffect(() => {
    const key = `${digitalImage || ''}|${userPhoto || ''}`;
    if (lastSrcRef.current === null) { lastSrcRef.current = key; return; }
    if (lastSrcRef.current === key) return;
    lastSrcRef.current = key;
    setImgError(false);
    setAttempt(0);
    // Note: imgLoaded is NOT reset here. See below.
  }, [digitalImage, userPhoto]);

  const wantedSrc = sized ? (candidates[attempt] || null) : null;

  // Hold the picture we already have until the next one is decoded.
  //
  // Dropping to opacity 0 the moment the source changed meant that stepping to
  // the next card in a binder blanked the largest thing on screen and faded it
  // back in. The component was not remounting - verified - but this alone made
  // it look like the whole page had reloaded.
  //
  // The new source loads behind the old one and only replaces it once the
  // browser has it, so a swap is a swap rather than a blank and a rebuild.
  const [shownSrc, setShownSrc] = useState(wantedSrc);
  useEffect(() => {
    if (!wantedSrc) { setShownSrc(null); return undefined; }
    if (wantedSrc === shownSrc) return undefined;

    // Nothing showing yet: no point holding an empty frame, swap immediately
    // and let the fade-in do its job.
    if (!shownSrc) { setShownSrc(wantedSrc); return undefined; }

    let cancelled = false;
    const pre = new Image();
    pre.onload = () => { if (!cancelled) { setShownSrc(wantedSrc); setImgLoaded(true); } };
    pre.onerror = () => { if (!cancelled) { setShownSrc(wantedSrc); } };
    pre.src = wantedSrc;
    return () => { cancelled = true; };
  }, [wantedSrc, shownSrc]);

  const currentSrc = shownSrc;
  const hasImage = currentSrc && !imgError;

  const handleError = () => {
    if (attempt + 1 < candidates.length) {
      setImgLoaded(false);
      // Drop what is on screen before stepping to the next candidate. The
      // preloader below exists to avoid blanking a good picture during a swap,
      // and there is no good picture here - holding a URL that just failed only
      // delays the working one behind a second load of it. Clearing it takes the
      // fast path in that effect, and the loading placeholder covers the gap.
      setShownSrc(null);
      setAttempt(attempt + 1);
    } else {
      setImgError(true);
    }
  };

  const containerStyle = {
    width: '100%',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme?.bgCard || '#1a1a1a',
    ...style,
  };

  // "Still coming" and "this card has no picture" are different facts and were
  // being drawn the same way. A grid whose artwork had not arrived yet rendered
  // the ImageOff verdict panel, so ten chase cards read as ten broken cards for
  // as long as the lookup took. Only draw the verdict once there is one.
  // inView is part of the test on purpose. A 331-card set grid would otherwise
  // put three hundred pulsing skeletons offscreen, animating for nobody.
  const resolving = pending || !sized || (inView && !!wantedSrc && !imgLoaded && !imgError);
  const noArt = !resolving && (imgError || !wantedSrc);

  return (
    <div style={containerStyle} onClick={onClick} ref={holderRef}>
      {hasImage && inView && (
        <img
          key={currentSrc}
          src={currentSrc}
          alt={name || 'Card'}
          loading="eager"
          decoding="async"
          // A cached image can finish decoding before React attaches onLoad, so
          // the handler never fires and the element stays at opacity 0 forever -
          // a grey box with no image, no error and no text fallback. Checking
          // `complete` on mount closes that gap.
          // Only while not yet loaded: an inline ref runs again on every render,
          // and setting the same state from it each time re-renders forever
          // (Maximum update depth) on a large grid.
          ref={(el) => {
            if (!imgLoaded && el && el.complete && el.naturalWidth > 0) setImgLoaded(true);
          }}
          onLoad={() => setImgLoaded(true)}
          onError={handleError}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: imgLoaded ? 1 : 0,
            transition: 'opacity 0.2s ease',
            ...imgStyle,
          }}
        />
      )}

      {/* Card-shaped, framed and slowly breathing, so a tile that is waiting
          looks like a tile that is waiting. It sits over the image while that
          fades in and unmounts the moment the picture is decoded. */}
      {resolving && (
        <div
          data-testid="card-image-loading"
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            border: `1px solid ${theme?.border || '#2e2e2e'}`,
            boxSizing: 'border-box',
            // bgHover is the theme's "one step off the card surface" colour in
            // both modes, which is exactly what a skeleton wants: visible
            // against the tile without reading as content.
            backgroundColor: theme?.bgHover || theme?.bgCard || '#252525',
            animation: 'cc-pulse 1.4s ease-in-out infinite',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* No artwork exists for this card, in either catalogue, and the owner has
          not photographed it.

          This is a statement about the card, not a failure of the app, and it
          has to read that way. The previous version put "No image yet" under the
          name in 7px italic grey on whatever was behind it, which looked exactly
          like something that had broken. A card the app identified correctly
          should not look like an error, so this is a deliberate panel: framed,
          centred, and captioned with what is actually true. */}
      {noArt && (
      <div style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: thumb ? 3 : 6,
        padding: thumb ? 8 : 14,
        textAlign: 'center',
        boxSizing: 'border-box',
        backgroundColor: theme?.bgCard || '#1a1a1a',
        border: `1px solid ${theme?.border || '#2e2e2e'}`,
        borderRadius: 'inherit',
      }}>
        <ImageOff
          size={thumb ? 16 : 22}
          color={theme?.textDisabled || '#5a5a5a'}
          strokeWidth={1.75}
        />

        {name && (
          <span style={{
            color: theme?.text || '#fff',
            fontSize: thumb ? 11 : 14,
            fontWeight: 700,
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            wordBreak: 'break-word',
          }}>
            {name}
          </span>
        )}

        {(number || setName) && (
          <span style={{
            color: theme?.textSecondary || '#8a8a8a',
            fontSize: thumb ? 9 : 11,
            fontWeight: 600,
            lineHeight: 1.25,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            maxWidth: '100%',
          }}>
            {[number ? `#${number}` : null, setName].filter(Boolean).join(' \u00b7 ')}
          </span>
        )}

        <span style={{
          color: theme?.textSecondary || '#8a8a8a',
          fontSize: thumb ? 8 : 10,
          fontWeight: 600,
          letterSpacing: '0.03em',
          marginTop: thumb ? 1 : 3,
          paddingTop: thumb ? 3 : 6,
          borderTop: `1px solid ${theme?.border || '#2e2e2e'}`,
          width: '100%',
        }}>
          Card found, no image
        </span>
      </div>
      )}
    </div>
  );
}
